import { Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { useState } from 'react';
import { useEditUserMutation } from '../../Redux/services/user';
import { encryptPassword } from '../../utils/cryptoUtils';
import { roleHierarchyMapper, regexPatterns } from '../../utils/constant';

const customSelectStyles = {
  placeholder: (provided) => ({
    ...provided,
    fontWeight: '500',
    opacity: '.7',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '1.15rem',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: '0.7rem',
    paddingBottom: '0.7rem',
    borderRadius: '1.15rem',
    backgroundColor: 'var(--bs-dropdown-bg)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'var(--bs-gray-300)',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    fontWeight: '500',
    color: 'var(--bs-gray-700)',
    fontSize: '1.1rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontWeight: '500',
    fontSize: '1.1rem',
    color: 'var(--bs-gray-700)',
  }),
  cursor: 'default',
  option: (styles, { isDisabled, isSelected }) => ({
    ...styles,
    backgroundColor: 'var(--bs-dropdown-bg)',
    color: isSelected ? 'var(--bs-text-gray-800)' : 'var(--bs-gray-700)',
    cursor: isDisabled ? 'not-allowed' : 'default',
    ':hover': {
      backgroundColor: 'var(--bs-component-hover-bg)',
    },
  }),
};

function EditUserModal({
  show, onHide, masterData = {}, userData = {}, isEditable,
}) {
  const [editUser, { isLoading }] = useEditUserMutation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: userData?.name ?? '',
      email: userData?.email ?? '',
      phoneNo: userData?.phoneNo ?? '',
      role: {
        label: roleHierarchyMapper[userData?.role?.name],
        value: userData?.role?.id,
      },
      status: {
        label: userData?.status?.name,
        value: userData?.status?.id,
      },
      password: userData?.password,
      projects: Array.isArray(userData?.projects)
        ? userData.projects.map((p) => ({
          value: p.id ?? p.value,
          label: p.name ?? p.label,
        }))
        : [],
    },
  });

  const onSubmit = async (data) => {
    const button = document.getElementById('kt_modal_new_card_submit');
    button.disabled = true;
    const encryptedPassword = encryptPassword(data.password);
    const projectIds = Array.isArray(data.projects)
      ? data.projects.map((project) => project.value)
      : [];
    const reqBody = {
      ...data,
      password: encryptedPassword,
      role: data.role.value,
      status: data.status.value,
      projects: projectIds,
    };

    const response = await editUser({ data: reqBody, id: userData?.id });
    button.disabled = false;
    if (response?.data?.success) {
      window.toastr.success('User updated successfully!');
      onHide();
    }
    if (response.error) {
      window.toastr.error(response?.error?.data?.error?.[0]?.message ?? response.error.message);
    }
  };

  const renderRoles = () => masterData?.data?.roles?.map((userRole) => ({
    label: roleHierarchyMapper[userRole?.name],
    value: userRole?.id,
  }));

  const renderStatus = () => masterData?.data?.userStatuses?.map((userStatus) => ({
    label: userStatus?.name,
    value: userStatus?.id,
  }));

  const projectOptions = masterData?.data.projects
    .map((project) => ({ value: project.id, label: project.name })) || [];
  return (
    <Modal show={show} onHide={() => onHide()} centered>
      <Modal.Header className="pb-2" closeButton>
        <Modal.Title>
          <h2>{!isEditable ? 'User Details' : 'Edit User'}</h2>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-4">

        <form id="kt_modal_new_card_form" className="form" onSubmit={handleSubmit(onSubmit)}>

          <div className="row mt-5">

            <div className="col-md-6 fv-row">

              <label htmlFor="name" className="required fs-6 fw-semibold form-label mb-2 assign-to">Full
                Name
              </label>
              <input
                {...register('name', {
                  required: { value: true, message: 'Name is required' },
                  validate: (value) => {
                    if (value.trim() === '') {
                      return 'Required';
                    }
                    if (value.trim().length < 1) {
                      return "Input can't be less than 1 characters";
                    }
                    if (value.trim().length > 50) {
                      return "Input can't be more then 50 characters";
                    }
                    return true;
                  },
                })}
                disabled={!isEditable}
                type="text"
                className="form-control"
              />
              {errors?.name
                 && <div className="text-danger small"> {errors.name?.message} </div>}
            </div>
            <div className="col-md-6 fv-row">

              <label htmlFor="email" className="required fs-6 fw-semibold form-label mb-2 assign-to">Email</label>
              <input
                {...register('email', {
                  required: { value: true, message: 'Email is required' },
                  pattern: { value: regexPatterns.USER_EMAIL, message: 'Please insert a valid email address' },
                })}
                disabled={!isEditable}
                id="email"
                type="text"
                className="form-control"
              />
              {errors?.email
                 && <div className="text-danger small"> {errors.email?.message} </div>}
            </div>
            <div className="col-md-6 fv-row mt-5">
              <label htmlFor="phoneNo" className="fs-6 fw-semibold form-label mb-2 assign-to">Phone No.</label>
              <input
                {...register('phoneNo', {
                  pattern: { value: regexPatterns.USER_PHONENUMBER, message: 'Invalid Phone no' },
                })}
                maxLength={10}
                disabled={!isEditable}
                type="tel"
                className="form-control"
              />
              {errors?.phoneNo
                 && <div className="text-danger small"> {errors.phoneNo?.message} </div>}
            </div>
            <div className="col-md-6 fv-row mt-5">

              <label htmlFor="role" className="required fs-6 fw-semibold form-label mb-2 assign-to">Role</label>
              <Controller
                name="role"
                rules={{
                  required: { value: true, message: 'Role is required' },
                }}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={renderRoles()}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    isDisabled={!isEditable}
                    styles={customSelectStyles}
                    placeholder="Role"
                  />
                )}
              />
              {errors?.role
                 && <div className="text-danger small"> {errors.role?.message} </div>}
            </div>

            <div className="col-md-6 mt-5 fv-row">

              <label htmlFor="status" className="required fs-6 fw-semibold form-label mb-2 assign-to">Status</label>
              <Controller
                name="status"
                rules={{
                  required: { value: true, message: 'Status is required' },
                }}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    components={{ IndicatorSeparator: () => null }}
                    options={renderStatus()}
                    placeholder="Status"
                    isDisabled={!isEditable}
                    styles={customSelectStyles}
                  />
                )}
              />
              {errors?.status
                 && <div className="text-danger small"> {errors.status?.message} </div>}
            </div>

            <div className="col-md-6 mt-5 fv-row identity">

              <label htmlFor="password" className="required fs-6 fw-semibold form-label mb-2 assign-to">Set Password</label>
              <input
                {...register('password', {
                  required: { value: true, message: 'Password is required' },
                  maxLength: { value: 20, message: 'Maximum length should be 20 characters' },
                  minLength: { value: 6, message: 'Minimum length should be 6 characters' },
                  pattern: { value: regexPatterns.USER_PASSWORD, message: 'Password must atleast have 1 alphabet and 1 number' },
                })}
                type={showPassword ? 'text' : 'password'}
                disabled={!isEditable}
                id="password-field"
                className="form-control rounded"
                autoComplete="off"
              />
              <span
                onClick={() => setShowPassword((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setShowPassword((prev) => !prev);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className={`fa fa-fw ${!showPassword ? 'fa-eye' : 'fa-eye-slash'} field-icon toggle-password" `}
              />
              {errors?.password
                 && <div className="text-danger small position-absolute"> {errors.password?.message} </div>}
            </div>

            <div className="col-md-12 mt-5 fv-row ">
              <label htmlFor="projects" className=" fs-6 fw-semibold form-label mb-2">Groups</label>
              <Controller
                name="projects"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={projectOptions}
                    styles={customSelectStyles}
                    isMulti
                    placeholder="Select Groups"
                  />
                )}
              />
              {errors?.projects && <div className="text-danger small">{errors?.projects?.message}</div>}
            </div>
          </div>

          <div className="text-left pt-5 mt-5">

            <button
              type="submit"
              id="kt_modal_new_card_submit"
              className="btn btn-sm btn-secondary"
              data-bs-dismiss="modal"
              disabled={!isEditable}
            >
              {!isLoading ? <span className="indicator-label">Save</span>
                : (
                  <span>
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                )}
            </button>
            <button
              onClick={() => onHide()}
              type="button"
              id="kt_modal_new_card_cancel"
              className="btn btn-sm btn-light ms-3"
              data-bs-dismiss="modal"
            >Cancel
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default EditUserModal;
